import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, GripVertical, Share2 } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const getStatusColor = (status) => {
  switch (status) {
    case 'in_progress': return 'bg-blue-100 text-blue-700';
    case 'done': return 'bg-emerald-100 text-emerald-700';
    default: return 'bg-stone-100 text-stone-600';
  }
};

function DraggableChild({ task, theme, onToggleComplete, onDelete, onUpdateStatus }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.task_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${theme?.colors.surface || 'bg-white'} border ${theme?.colors.border || 'border-stone-200'} rounded-lg p-3 sm:p-4 ${isDragging ? 'shadow-lg z-50' : ''}`}
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="flex items-center gap-2 sm:gap-3">
        <button
          {...attributes}
          {...listeners}
          className={`p-0.5 cursor-grab active:cursor-grabbing flex-shrink-0 ${theme?.colors.textSecondary || 'text-stone-400'} hover:opacity-70 touch-none`}
          data-testid={`drag-handle-${task.task_id}`}
          tabIndex={-1}
        >
          <GripVertical className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </button>

        <input
          type="checkbox"
          checked={task.completed}
          onChange={() => onToggleComplete(task.task_id, task.completed)}
          className="task-checkbox"
          data-testid={`task-checkbox-${task.task_id}`}
        />

        <span
          className={`flex-1 text-sm sm:text-base ${
            task.completed ? `line-through ${theme?.colors.textSecondary || 'text-stone-400'}` : (theme?.colors.textPrimary || 'text-stone-900')
          }`}
          style={{ fontFamily: 'Manrope, sans-serif' }}
          data-testid={`task-title-${task.task_id}`}
        >
          {task.title}
        </span>

        <select
          value={task.status}
          onChange={(e) => onUpdateStatus(task.task_id, e.target.value)}
          disabled={task.completed}
          data-testid={`task-status-${task.task_id}`}
          className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full font-medium border-0 cursor-pointer ${getStatusColor(task.status)} disabled:opacity-50`}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          <option value="todo">To Do</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>

        <button
          onClick={() => onDelete(task.task_id)}
          className={`p-1 hover:bg-red-50/20 rounded ${theme?.colors.textSecondary || 'text-stone-400'} hover:text-red-500 transition-colors flex-shrink-0`}
          data-testid={`delete-task-${task.task_id}`}
        >
          <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        </button>
      </div>
    </div>
  );
}

function TaskList({ task, allTasks, onToggleComplete, onDelete, onUpdateStatus, theme, onReorder, onShareWin }) {
  const [expanded, setExpanded] = useState(true);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const childTasks = allTasks.filter(t => t.parent_id === task.task_id);
  const hasChildren = childTasks.length > 0;
  const completedChildren = childTasks.filter(c => c.completed).length;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = childTasks.findIndex(t => t.task_id === active.id);
      const newIndex = childTasks.findIndex(t => t.task_id === over.id);
      const reordered = arrayMove(childTasks, oldIndex, newIndex);
      onReorder(reordered.map(t => t.task_id));
    }
  };

  return (
    <div
      className={`${theme?.colors.surface || 'bg-white'} border ${theme?.colors.border || 'border-stone-200'} rounded-xl shadow-sm overflow-hidden`}
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="p-4 sm:p-6">
        <div className="flex items-start gap-3">
          {hasChildren && (
            <button
              onClick={() => setExpanded(!expanded)}
              className={`p-1 rounded transition-colors flex-shrink-0 ${theme?.colors.textSecondary || 'text-stone-500'}`}
              data-testid={`toggle-expand-${task.task_id}`}
            >
              {expanded ? <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" /> : <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          )}

          <input
            type="checkbox"
            checked={task.completed}
            onChange={() => onToggleComplete(task.task_id, task.completed)}
            className="task-checkbox"
            data-testid={`task-checkbox-${task.task_id}`}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3
                className={`text-sm sm:text-lg font-medium leading-snug ${
                  task.completed ? `line-through ${theme?.colors.textSecondary || 'text-stone-400'}` : (theme?.colors.textPrimary || 'text-stone-900')
                }`}
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid={`task-title-${task.task_id}`}
              >
                {task.title}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0">
                {hasChildren && completedChildren > 0 && (
                  <button
                    onClick={() => onShareWin(task, childTasks)}
                    className={`p-1 sm:p-2 rounded ${theme?.colors.textSecondary || 'text-stone-400'} hover:text-[#E07A5F] transition-colors`}
                    data-testid={`share-win-${task.task_id}`}
                    title="Share your win"
                  >
                    <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </button>
                )}
                <button
                  onClick={() => onDelete(task.task_id)}
                  className={`p-1 sm:p-2 hover:bg-red-50/20 rounded ${theme?.colors.textSecondary || 'text-stone-400'} hover:text-red-500 transition-colors`}
                  data-testid={`delete-task-${task.task_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={task.status}
                onChange={(e) => onUpdateStatus(task.task_id, e.target.value)}
                disabled={task.completed}
                data-testid={`task-status-${task.task_id}`}
                className={`text-xs sm:text-sm px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium border-0 cursor-pointer transition-colors ${getStatusColor(task.status)} disabled:opacity-50 disabled:cursor-not-allowed`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <option value="todo">To Do</option>
                <option value="in_progress">In Progress</option>
                <option value="done">Done</option>
              </select>
              {hasChildren && (
                <span className={`text-xs ${theme?.colors.textSecondary || 'text-stone-400'} ml-1`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {childTasks.filter(c => c.completed).length}/{childTasks.length} subtasks
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasChildren && expanded && (
        <div className={`border-t ${theme?.colors.border || 'border-stone-100'} ${theme?.colors.bg || 'bg-stone-50/50'} p-3 sm:p-5`}>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={childTasks.map(t => t.task_id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {childTasks.map(child => (
                  <DraggableChild
                    key={child.task_id}
                    task={child}
                    theme={theme}
                    onToggleComplete={onToggleComplete}
                    onDelete={onDelete}
                    onUpdateStatus={onUpdateStatus}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

export default TaskList;
